import { PageHero } from '@components/common';
import { getServiceCard } from '@data/serviceCards';
import {
  Target,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Lightbulb,
  Compass,
  FileText,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { CALENDLY_URL } from '@constants';

const offerings = [
  {
    icon: Compass,
    title: 'Security Roadmap Development',
    description:
      'Create a multi-year security strategy aligned with your business objectives, technology roadmap, and risk appetite.',
  },
  {
    icon: FileText,
    title: 'Policy & Governance Framework',
    description:
      'Establish comprehensive security policies, standards, and procedures that guide your organisation.',
  },
  {
    icon: Target,
    title: 'Security Investment Planning',
    description:
      'Prioritise security spending based on risk, business impact, and available budget.',
  },
  {
    icon: TrendingUp,
    title: 'Metrics & KPI Framework',
    description:
      'Define meaningful security metrics that demonstrate progress and value to leadership.',
  },
];

const benefits = [
  'Clear direction for security initiatives with executive buy-in',
  'Reduced wasted spending on reactive, disconnected security tools',
  'Alignment between security and business objectives',
  'Proactive security posture rather than reactive firefighting',
  'Board-level confidence in security programme direction',
  'Foundation for compliance and regulatory requirements',
];

const process = [
  {
    step: '01',
    title: 'Current State Assessment',
    description:
      'We evaluate your existing security posture, capabilities, and gaps to establish a baseline.',
  },
  {
    step: '02',
    title: 'Business Context Discovery',
    description:
      'We understand your business strategy, growth plans, and risk tolerance to align security accordingly.',
  },
  {
    step: '03',
    title: 'Strategy Development',
    description:
      'We create a comprehensive security strategy with clear objectives, initiatives, and timelines.',
  },
  {
    step: '04',
    title: 'Roadmap & Implementation',
    description:
      'We deliver a prioritised implementation roadmap with quick wins and long-term initiatives.',
  },
];

export default function SecurityStrategyPage() {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Security Strategy Development',
    description:
      'Build a comprehensive cybersecurity roadmap that protects your business while enabling growth and innovation',
    url: 'https://thecisonetwork.com/services/security-strategy',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <PageHero
        title="Security Strategy Development"
        subtitle="Build a comprehensive cybersecurity roadmap that protects your business while enabling growth and innovation"
        eyebrow="Our Services"
        backgroundImage={getServiceCard('security-strategy')?.image}
        backgroundAlt={getServiceCard('security-strategy')?.imageAlt}
        seoTitle="Security Strategy Development | The CISO Network"
        seoDescription="Develop a comprehensive cybersecurity strategy with our fractional CISOs. Multi-year roadmaps, governance frameworks, and security investment planning."
      />

      {/* Overview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                A Well-Defined Strategy is Your Foundation
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Without a clear security strategy, organisations waste resources on disconnected
                  tools and reactive measures. They struggle to communicate security value to the{' '}
                  <Link to="/services/board-reporting" className="text-secondary hover:underline">
                    board
                  </Link>{' '}
                  and find themselves constantly firefighting rather than building sustainable
                  security capabilities.
                </p>
                <p>
                  Our fractional CISOs help you develop a comprehensive security strategy that
                  protects your critical assets while supporting business objectives. We create
                  practical roadmaps that balance security needs with budget realities and
                  demonstrate clear value to stakeholders.
                </p>
                <p>
                  Whether you're building a security programme from scratch or refining an existing
                  strategy, we bring the expertise to create a plan that works for your specific
                  context.
                </p>
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <div className="flex items-center gap-4 mb-6 justify-center">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center">
                  <Lightbulb className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground">
                  When You Need This
                </h3>
              </div>
              <div className="flex justify-center">
                <ul className="space-y-4 inline-block text-left">
                  {[
                    'Building a security programme from scratch',
                    'Preparing for significant business growth or change',
                    'Struggling to justify security spending',
                    'Security initiatives lack direction or coordination',
                    <>
                      Preparing for{' '}
                      <Link
                        to="/packages/ma-cyber-due-diligence"
                        className="text-secondary hover:underline"
                      >
                        investment, acquisition, or IPO
                      </Link>
                    </>,
                    'Need to align security with business strategy',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Offerings */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              What We Deliver
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Comprehensive strategy development covering all aspects of your security programme
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {offerings.map((offering, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-border">
                <div className="h-12 w-12 rounded-xl bg-teal/10 flex items-center justify-center mb-6">
                  <offering.icon className="h-6 w-6 text-teal" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                  {offering.title}
                </h3>
                <p className="text-muted-foreground">{offering.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid sm:grid-cols-2 gap-6">
                {benefits.map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-teal font-bold text-sm">{i + 1}</span>
                    </div>
                    <p className="text-muted-foreground">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                The Benefits of Strategic Security
              </h2>
              <p className="text-muted-foreground mb-6">
                A well-executed security strategy transforms security from a cost centre into a
                business enabler. Our clients report significant improvements in operational
                efficiency, risk reduction, and stakeholder confidence.
              </p>
              <div className="p-6 bg-background rounded-xl border border-border">
                <h4 className="font-heading font-semibold text-foreground mb-3">
                  Typical Engagement
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="text-foreground">4-8 weeks</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Deliverable</span>
                    <span className="text-foreground">Strategy & roadmap</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Output</span>
                    <span className="text-foreground">Board-ready plan</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Our Approach
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              A proven methodology for developing strategies that work in practice
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-heading font-bold text-teal/10 mb-4">{step.step}</div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-muted-foreground text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-8 text-center">
              Typical Deliverables
            </h2>
            <div className="space-y-4">
              {[
                {
                  title: 'Security Strategy Document',
                  description:
                    'Comprehensive strategy covering objectives, principles, and approach',
                },
                {
                  title: '3-Year Implementation Roadmap',
                  description:
                    'Prioritised initiatives with timelines, dependencies, and resource requirements',
                },
                {
                  title: 'Policy Library',
                  description: 'Core security policies and standards tailored to your organisation',
                },
                {
                  title: 'Executive Presentation Materials',
                  description: 'Board-ready materials to communicate strategy and gain buy-in',
                },
                {
                  title: 'Metrics Framework',
                  description:
                    'KPIs and reporting structure to track progress and demonstrate value',
                },
              ].map((deliverable, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-background rounded-xl">
                  <div className="h-6 w-6 rounded-full bg-teal flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground">
                      {deliverable.title}
                    </h4>
                    <p className="text-muted-foreground text-sm">{deliverable.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Develop Your Security Strategy?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a discovery call to discuss your strategy needs and how our{' '}
            <Link to="/what-is-a-fractional-ciso" className="text-teal hover:underline">
              fractional CISOs
            </Link>{' '}
            can help.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
